import React from 'react';
import { EditIcon3, DeleteIcon, StarIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { WorkStylesEditModal, DeleteSectionModal } from 'components';
import { useToast, useWorkCultureTraitsEdit, useWorkStylesDelete, WorkStylesPayload } from 'hooks';

interface WorkStylesSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['workStyles'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['workStyles']) => void;
  disabled?: boolean;
}

const WorkStylesSection: React.FC<WorkStylesSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onDelete,
  onSave,
  disabled
}) => {
  const { openToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const {
    submitWorkCultureTraits,
    isSubmitting
  } = useWorkCultureTraitsEdit(assessmentId);
  const {
    deleteWorkStyles,
  } = useWorkStylesDelete(assessmentId);

  const renderStars = (rating: number, maxRating: number = 7) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(maxRating)].map((_, index) => (
          <span key={index} className={`text-[22px] ${index < rating ? 'text-[#f4b400]' : 'text-[#dfe1e6]'}`}>
            <StarIcon />
          </span>
        ))}
      </div>
    );
  };

  const onSubmit = (formData: JobCriteriaData['workStyles']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    const payload: WorkStylesPayload = {
      assessment_id: assessmentId,
      work_style1: formData.workStyles[0]?.description || '',
      work_style1_rating: formData.workStyles[0]?.rating || 0,
      work_style2: formData.workStyles[1]?.description || '',
      work_style2_rating: formData.workStyles[1]?.rating || 0,
      work_style3: formData.workStyles[2]?.description || '',
      work_style3_rating: formData.workStyles[2]?.rating || 0,
      work_style4: formData.workStyles[3]?.description || '',
      work_style4_rating: formData.workStyles[3]?.rating || 0,
    };
    submitWorkCultureTraits(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsEditModalOpen(false);
      },
      onError: (_err) => {
        setIsEditModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required for deletion",
        type: "error",
        timeOut: 5000,
      });
      return;
    }
    deleteWorkStyles({ assessment_id: assessmentId }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Work Styles</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsEditModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="mb-5 text-sm font-normal leading-5 text-[#1D2939]">
        Here's a ranked list of the most relevant work styles for strong performance in this role, ordered from highest to lowest priority in terms of importance.
      </p>

      <div className="flex flex-col">
        {data.workStyles.map((style, index) => (
          <div key={index} className="flex flex-col gap-2 mb-2.5">
            <div className="text-base font-semibold mb-2.5">{index + 1}. {style.description}</div>
            <div className="mb-2.5">
              {renderStars(style.rating, 7)}
            </div>
          </div>
        ))}
      </div>

      <WorkStylesEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Work Styles"
      />
    </section>
  );
};

export default WorkStylesSection;
